import { Injectable } from '@angular/core';
import { Submission, SubmissionStatus, DeletedSubmission } from '../model/paper';
import { Track } from '../model/track';
import { HttpClient, HttpHeaders, HttpRequest, HttpEventType } from '@angular/common/http';
import { map, catchError, shareReplay, finalize } from 'rxjs/operators';
import { plainToClass, plainToClassFromExist } from 'class-transformer';
import { Environment } from '../environment/environment';
import { forkJoin, Observable, of as ObservableFrom, of, Subject } from 'rxjs';
import { PrintingProblem } from '../model/printing.problem';
import { FilesService } from './files.service';
import { Response } from '../model/response';
import { CacheService } from './cache.service';
import { SubmissionFilter } from '../model/submission.filter';
import { FileRules } from '../model/file.rules';
import { Pagination } from '../model/pagination';
import { DEFAULT_PAGINATION_OPTIONS, SHOW_ALL_PAGINATION_OPTIONS, PaginationOptions } from '../model/pagination.options';
import { Review } from '../model/paper';
import { DiscussionMessage } from '../model/discussionMessage';
import { Rebuttal } from '../model/rebuttal';
import { File } from '../model/file';
import { SubmissionCoverage } from './publication.service';
import { Target } from '@angular/compiler';

// ---- Registro de submissões em lote (Função Extra) -------------------------
// Uma linha da planilha enviada ao diagnóstico. `key` é do front (nº da linha)
// e volta intacta — casar por ela evita depender de normalização de título.
export interface BulkSubmissionLookupRow { key: number; email: string; title: string; }

// Autor encontrado. Mesma forma do lookup de autores (`BulkAuthorLookupResult`).
export interface BulkSubmissionLookupAuthor {
  requestedEmail: string;
  id: number;
  firstName: string;
  lastName: string;
  matchedField: 'email' | 'first_alternative_email' | 'second_alternative_email';
}

export interface BulkSubmissionLookupResult {
  authors: BulkSubmissionLookupAuthor[];
  /** Linhas que já têm submissão no evento com o mesmo título e autor.
   *  `trackId` vem junto porque a busca varre o evento inteiro: a submissão
   *  encontrada pode estar em trilha diferente da escolhida na tela. */
  existing: { key: number; submissionId: number; trackId: number }[];
}

export interface BulkSubmissionCreated { id: number; title: string; track: number; notified: boolean; }

@Injectable({
  providedIn: 'root'
})
export class SubmissionsService {
  constructor(
    private http: HttpClient,
    private filesService: FilesService,
    private cache: CacheService
  ) { }

  // ─── Dedup + freshness-window de requests ──────────────────────────────
  // Dois mecanismos combinados:
  //
  // 1) `inFlight` — observables EM VÔO compartilhados via `shareReplay(1)`.
  //    Resolve race de chamadas TRULY concorrentes (mesmo tick / Promise
  //    microtask) que veem cache vazio antes do 1º response chegar.
  //
  // 2) `lastFetchedAt` — timestamp por (id|admin). Durante a janela de
  //    `STALE_AFTER_MS`, ignoramos `refreshCache=true` e servimos cache.
  //    Por quê: na abertura da página de detalhe, vários componentes
  //    (paper-info, submission-view, file-rules-*) chamam getSubmission
  //    com refreshCache=true em ngOnInit. Sem freshness-window, cada um
  //    refazia o HTTP de 71.8 kB ainda que o anterior tivesse chegado <1s
  //    atrás. A janela é curta o suficiente pra não bloquear o caso
  //    "edita → navega → quero o valor atualizado" (esse leva >2s).
  private inFlight = new Map<string, Observable<Submission>>();
  private lastFetchedAt = new Map<string, number>();
  private static readonly STALE_AFTER_MS = 2000;

  /** Pré-popula o cache local (submissão + freshness window). Após isso,
   *  `getSubmission` retorna do cache sem HTTP na janela `STALE_AFTER_MS`.
   *  Prime pra AMBAS as chaves (admin e plain) pra cobrir qualquer caminho
   *  de consumo. */
  primeSubmissionCache(submission: Submission): void {
    this.cache.setValue(this.cache.submissions, submission.id.toString(), submission);
    // Freshness "eterna" — o v2 processa 500 subs em ~minutos, muito além
    // da janela padrão de 2s. Setar `Date.now() + 1e10` (~317 anos) garante
    // cache-hit até o refresh/fechamento da página.
    const eternity = Date.now() + 1e10;
    this.lastFetchedAt.set(`${submission.id}|admin`, eternity);
    this.lastFetchedAt.set(`${submission.id}|plain`, eternity);
  }

  /**
   * Diagnóstico da planilha de submissões em lote: quais e-mails têm conta e
   * quais linhas já existem como submissão no evento. Uma ida só.
   */
  bulkLookup(eventId: number, rows: BulkSubmissionLookupRow[]): Observable<BulkSubmissionLookupResult> {
    return this.http
      .post<Response>(`${Environment.urls.API}/core/event/${eventId}/submissions/lookup/`, { rows })
      .pipe(map(v => (v.data || { authors: [], existing: [] }) as BulkSubmissionLookupResult));
  }

  /**
   * Cria UMA submissão (pendente, um autor, sem arquivo). Uma por requisição,
   * de propósito — o componente atualiza cada linha conforme a sua resolve.
   */
  bulkCreate(eventId: number, payload: {
    track: number, email: string, title: string, abstract: string, notify: boolean
  }): Observable<BulkSubmissionCreated> {
    return this.http
      .post<Response>(`${Environment.urls.API}/core/event/${eventId}/submissions/create/`, payload)
      .pipe(map(v => v.data as BulkSubmissionCreated));
  }

  getSubmission(idRaw: number | string, refreshCache = false, isAdminRoute?: Boolean): Observable<Submission> {
    // Normaliza tipo (string vs number) pra garantir hit no inFlight/cache.
    const id = typeof idRaw === 'string' ? parseInt(idRaw, 10) : idRaw;
    const params = { params: {} };
    if (isAdminRoute) {
      params.params['isAdminRoute'] = isAdminRoute;
    }

    const flightKey = `${id}|${isAdminRoute ? 'admin' : 'plain'}`;
    const cached = this.cache.getValue(this.cache.submissions, id.toString());
    const fetchedAt = this.lastFetchedAt.get(flightKey) || 0;
    const isFresh = (Date.now() - fetchedAt) < SubmissionsService.STALE_AFTER_MS;

    // Cache de valor: serve se está fresco (sempre) OU se refreshCache=false.
    if (cached && (!refreshCache || isFresh)) {
      return ObservableFrom(cached);
    }

    // Dedup de chamadas concorrentes (mesmo se refreshCache=true em ambas).
    const existing = this.inFlight.get(flightKey);
    if (existing) {
      return existing;
    }

    const request$ = this.http.get<Response>(`${Environment.urls.API}/core/submission/${id}/`, params)
      .pipe(map(v => plainToClass(Submission, v.data as Submission)))
      .pipe(map(submission => {
        this.lastFetchedAt.set(flightKey, Date.now());
        return this.cache
          .setValue(this.cache.submissions, submission.id.toString(), submission)
          .get(submission.id.toString());
      }))
      .pipe(
        finalize(() => this.inFlight.delete(flightKey)),
        shareReplay(1),
      );

    this.inFlight.set(flightKey, request$);
    return request$;
  }

  getUserSubmissions(options: PaginationOptions = DEFAULT_PAGINATION_OPTIONS,
    submFilter: SubmissionFilter = new SubmissionFilter(),
    event?: number, ignoreStatus = false): Observable<Pagination<Submission>> {
    const params = { params: {} };

    if (options.pageNumber) {
      params.params['pageNumber'] = String(options.pageNumber);
    }

    if (options.show) {
      params.params['show'] = String(options.show);
    }

    if (!submFilter.isEmpty()) {
      params.params['orderBy'] = submFilter.orderBy;
      params.params['sortBy'] = submFilter.sortBy;

      if (!ignoreStatus && submFilter.status.length > 0) {
        params.params['status'] = submFilter.status.map(s => s);
      }

      params.params['missingManuscripts'] = submFilter.missingManuscripts;
    }

    if (options.pageSize) {
      params.params['pageSize'] = String(options.pageSize);
    }

    if (event) {
      params.params['event'] = String(event);
    }

    return this.http.get<Response>(`${Environment.urls.API}/core/submission/byUser/`, params)
      .pipe(map(v => plainToClassFromExist(new Pagination<Submission>(Submission), v.data)))
      .pipe(map(v => {
        this.cache.setAll(
          this.cache.submissions,
          v.items.map(submission => ({ key: submission.id.toString(), value: submission }))
        );

        return v;
      }));
  }

  getUserIDSubmissions(options: PaginationOptions = DEFAULT_PAGINATION_OPTIONS,
    submFilter: SubmissionFilter = new SubmissionFilter(),
    event?: number, userId?:number, ignoreStatus = false): Observable<Pagination<Submission>> {
    const params = { params: {} };

    if (options.pageNumber) {
      params.params['pageNumber'] = String(options.pageNumber);
    }

    if (options.show) {
      params.params['show'] = String(options.show);
    }

    if (!submFilter.isEmpty()) {
      params.params['orderBy'] = submFilter.orderBy;
      params.params['sortBy'] = submFilter.sortBy;

      if (!ignoreStatus && submFilter.status.length > 0) {
        params.params['status'] = submFilter.status.map(s => s);
      }

      params.params['missingManuscripts'] = submFilter.missingManuscripts;
    }

    if (options.pageSize) {
      params.params['pageSize'] = String(options.pageSize);
    }

    if (event) {
      params.params['event'] = String(event);
    }

    return this.http.get<Response>(`${Environment.urls.API}/core/submission/byUser/${userId}/`, params)
      .pipe(map(v => plainToClassFromExist(new Pagination<Submission>(Submission), v.data)))
      .pipe(map(v => {
        this.cache.setAll(
          this.cache.submissions,
          v.items.map(submission => ({ key: submission.id.toString(), value: submission }))
        );

        return v;
      }));
  }

  getUserSubmissionsFromEvent(event: number) {
    return this.getUserSubmissions(SHOW_ALL_PAGINATION_OPTIONS, new SubmissionFilter(), event);
  }

  getSessionsSubmissionsBySessionChair(event: number, user: number, show?: string): Observable<Pagination<Submission>> {
    const params = { params: {} };

    if (event) {
      params.params['event'] = String(event);
    }

    if (user) {
      params.params['user'] = String(user);
    }

    if (show) {
      params.params['show'] = String(show);
    }

    return this.http.get<Response>(`${Environment.urls.API}/core/submission/byUserSessionChair/`, params)
      .pipe(map(v => plainToClassFromExist(new Pagination<Submission>(Submission), v.data)))
      .pipe(map(v => {
        this.cache.setAll(
          this.cache.submissions,
          v.items.map(submission => ({ key: submission.id.toString(), value: submission }))
        );

        return v;
      }));
    }

    getSubmissionsBySession(sessionId: number): Observable<Array<Submission>> {
      return this.http.get<Response>(Environment.urls.API + '/core/submission/bySession/' + sessionId + '/')
      .pipe(map(v => plainToClass(Submission, v.data as Array<Submission>)));
    }

  getUserIDSubmissionsFromEvent(event: number, userId: number) {
    return this.getUserIDSubmissions(SHOW_ALL_PAGINATION_OPTIONS, new SubmissionFilter(), event, userId);
  }

  getSubmissionsByEvent(eventId: number, params: { detailed?, toAssign?, status?, track?, paperGroup? }): Observable<Submission[]> {
    return this.http.get<Response>(Environment.urls.API + '/core/submission/byEvent/' + eventId + '/', { params })
      .pipe(map(v => plainToClass(Submission, v.data.items as Array<Submission>)))
      .pipe(map(submissions => {
        if (params.detailed) {
          submissions.forEach(submission =>
            submission.reviews.forEach(review => {
              (<Review>review).submission = submission;
            })
          );
        }

        if (!params.toAssign) {
        this.cache.setAll(
          this.cache.submissions,
          submissions.map(submission => ({ key: submission.id.toString(), value: submission }))
        );
        }
        return submissions;
      }));
  }

  getSubmissionsByEventFull(eventId: number, params: { detailed?, toAssign?, status?, track?, paperGroup?, reviews? }): Observable<Submission[]> {
    return this.http.get<Response>(Environment.urls.API + '/core/submission/byEvent/' + eventId + '/full/', { params })
      .pipe(map(v => plainToClass(Submission, v.data as Array<Submission>)))
      .pipe(map(submissions => {
        if (params.detailed) {
          submissions.forEach(submission =>
            submission.reviews.forEach(review => {
              (<Review>review).submission = submission;
            })
          );
        }

        if (!params.toAssign) {
        this.cache.setAll(
          this.cache.submissions,
          submissions.map(submission => ({ key: submission.id.toString(), value: submission }))
        );
        }

        return submissions;
      }));
  }

  getSubmissionsByEventMinimum(id: number): Observable<Submission[]> {
    return this.http.get<Response>(`${Environment.urls.API}/core/submission/byEvent/${id}/minimum/`, {}).pipe(
      map(v => v.data as Submission[])
    );
  }

  public getReportPrintingProblem(submissionId: number): Observable<Array<PrintingProblem>> {
    return this.http.get<Response>(`${Environment.urls.API}/core/submission/${submissionId}/report/problem/printing/`)
      .pipe(map(v => plainToClass(PrintingProblem, v.data as Array<PrintingProblem>)));
  }

  getSubmissionReviewersByInterest(submissionId: number): Observable<Submission> {
    return this.http
      .get<Response>(`${Environment.urls.API}/core/submission/${submissionId}/reviewersByInterest/`)
      .pipe(map(value => plainToClass(Submission, value.data as Submission)));
  }

  getAllSubmissionsByEvent(eventId: number): Observable<Array<Submission>> {
    return this.http.get<Response>(Environment.urls.API + '/core/submission/byEvent/' + eventId + '/all/')
      .pipe(map(v => plainToClass(Submission, v.data as Array<Submission>)));
  }

  getAllSubmissionsWithoutPaperGroupByEvent(eventId: number): Observable<Array<Submission>> {
    return this.getAllSubmissionsByEvent(eventId).pipe(map(v => v.filter(submission => !submission.paperGroup)));
  }

  getAllSubmissionsWithoutTPCGroupByEvent(eventId: number): Observable<Array<Submission>> {
    return this.getAllSubmissionsByEvent(eventId).pipe(map(v => v.filter(submission => !submission.tpcGroup)));
  }

  getPagedSubmissionsByEvent(
    eventId: number,
    options: PaginationOptions = DEFAULT_PAGINATION_OPTIONS,
    subFilter: SubmissionFilter = new SubmissionFilter()
  ): Observable<Pagination<Submission>> {
    const params = { params: {} };

    if (options.pageNumber) {
      params.params['pageNumber'] = String(options.pageNumber);
    }

    if (options.pageSize) {
      params.params['pageSize'] = String(options.pageSize);
    }

    if (options.show) {
      params.params['show'] = String(options.show);
    }

    if (!subFilter.isEmpty()) {
      params.params['orderBy'] = subFilter.orderBy;
      params.params['sortBy'] = subFilter.sortBy;

      if (subFilter.status.length > 0) {
        params.params['status'] = subFilter.status.map(s => s);
      }
    }

    return this.http.get<Response>(`${Environment.urls.API}/core/submission/byEvent/${eventId}/`, params)
      .pipe(map(v => plainToClassFromExist(new Pagination<Submission>(Submission), v.data)))
      .pipe(map(v => {
        this.cache.setAll(
          this.cache.submissions,
          v.items.map(submission => ({ key: submission.id.toString(), value: submission }))
        );

        return v;
      }));
  }

  createSubmission(data: any): Observable<Submission> {
    return this.http.post<Response>(`${Environment.urls.API}/core/submission/`, data)
      .pipe(map(v => plainToClass(Submission, v.data as Submission)))
      .pipe(map(submission =>
        this.cache
          .setValue(this.cache.submissions, submission.id.toString(), submission)
          .get(submission.id.toString())
      ));
  }

  editSubmission(data: any, id: number): Observable<Submission> {
    return this.http.put<Response>(`${Environment.urls.API}/core/submission/${id}/`, data)
      .pipe(map(v => plainToClass(Submission, v.data as Submission)))
      .pipe(map(submission =>
        this.cache
          .setValue(this.cache.submissions, submission.id.toString(), submission)
          .get(submission.id.toString())
      ));
  }

  /**
   * Endpoint dedicado para o tri-estado no-show (`no_show`). Existe pra
   * que o session chair (que NÃO tem `core.update_submission` cheia) possa
   * marcar Apresentado / No-show / Indefinido na página "Sessões que
   * coordeno" sem ganhar poderes sobre outros campos da submissão.
   */
  editSubmissionNoShow(id: number, value: boolean | null): Observable<Response> {
    return this.http.put<Response>(`${Environment.urls.API}/core/submission/${id}/noShow/`, { noShow: value });
  }

  editMinimumSubmission(data: any, id: number): Observable<Submission> {
    return this.http.put<Response>(`${Environment.urls.API}/core/submission/minimum/${id}/`, data)
      .pipe(map(v => plainToClass(Submission, v.data as Submission)))
      .pipe(map(submission =>
        this.cache
          .setValue(this.cache.submissions, submission.id.toString(), submission)
          .get(submission.id.toString())
      ));
  }

  changeTracks(data: {submission_id: number, target_track_id: number, swap: {}}): Observable<Submission> {
    return this.http.patch<Response>(`${Environment.urls.API}/core/trackFile/changeTracks/`, data)
      .pipe(map(v => plainToClass(Submission, v.data as Submission)));
  }

  submitFiles(files: any, submissionID: number, track: Track): Observable<Array<{ fileRule: FileRules, status: number }>> {
    // return: do forkJoin, um array de {
    //  FileRules,
    //  status: number, -1 => equivale a erro no upload daquele tipo de arquivo.
    // }
    const uploadStatus: Array<{fileRule: FileRules, status: number}> = [];
    for (const key in files) {
      if (files.hasOwnProperty(key)) {
        const fileRule = track.trackFiles.find(e => e.name === key);

        uploadStatus.push({ fileRule, status: 0 });
      }
    }

    const subj = new Subject<Array<{ fileRule: FileRules, status: number }>>();
    for (const key in files) {
      if (files.hasOwnProperty(key)) {
        const file = files[key][0];
        const fileRule = track.trackFiles.find(e => e.name === key);

        if (fileRule) {
          this.filesService.uploadSubmissionFile(file, submissionID, fileRule).pipe(
            catchError(error => ObservableFrom(-1)),
          ).subscribe(v => {
            uploadStatus.find(u => u.fileRule === fileRule).status = v;

            subj.next(uploadStatus);
            if (v === 100) {
              this.submitFilesCompleted(subj, uploadStatus);
            }
          }, () => {}, () => {
            this.submitFilesCompleted(subj, uploadStatus);
          });
        } else {
          console.log('rule not found');
        }
      }
    }

    return subj.asObservable();
  }

  // When a file has finished uploading, check if all files have completed.
  private submitFilesCompleted(
    subject: Subject<Array<{ fileRule: FileRules, status: number }>>,
    uploadStatus: Array<{ fileRule: FileRules, status: number }>
  ) {
    const notAllUploadsComplete = !!uploadStatus.find(u => u.status !== 100);
    if (!notAllUploadsComplete) {
      subject.complete();
    }
  }

  public getSubmissionsToClaim(eventId: number, userId: number) {
    return this.http.get<Response>(`${Environment.urls.API}/core/claimReview/${eventId}/${userId}/`)
      .pipe(map(v => plainToClass(Submission, v.data as Array<Submission>)));
  }

  public updateClaimSubmissions(eventId: number, userId: number, submissionsId: number[]) {
    return this.http.put<Response>(`${Environment.urls.API}/core/claimReview/${eventId}/${userId}/`, { submissionsId })
      .pipe(map(v => plainToClass(Submission, v.data as Array<Submission>)));
  }

  public assignSubmissionStatus(
    submissionsIds: Array<number>, status: SubmissionStatus, event: number
  ): Observable<{ [submission: number]: boolean }> {
    return this.http.put<Response>(`${Environment.urls.API}/core/submissionList/AcceptOrReject/${event}/`, { submissionsIds, status })
      .pipe(map(v => v.data));
  }

  public withdrawSubmission(submission: number, withdrawReason: string): Observable<Submission> {
    return this.http.put<Response>(`${Environment.urls.API}/core/submission/${submission}/withdraw/`, { withdrawReason })
      .pipe(map(v => plainToClass(Submission, v.data as Submission)));
  }

  public deleteSubmission(submission: number, deleteReason: string): Observable<Submission> {
    return this.http.put<Response>(`${Environment.urls.API}/core/submission/${submission}/deleteSubmission/`, { deleteReason })
      .pipe(map(v => plainToClass(Submission, v.data as Submission)));
  }

  public permanentlyDeleteSubmission(submission: number, deleteReason: string): Observable<Submission> {
    return this.http.put<Response>(`${Environment.urls.API}/core/submission/${submission}/permanentlydeleteSubmission/`, { deleteReason })
      .pipe(map(v => plainToClass(Submission, v.data as Submission)));
  }

  public recoverySubmission(submission: number, previousStatus: string): Observable<Submission> {
    const params = {'previousStatus': previousStatus};
    return this.http.put<Response>(`${Environment.urls.API}/core/submission/${submission}/recoverySubmission/`, params)
      .pipe(map(v => plainToClass(Submission, v.data as Submission)));
  }
 
  public getDeletedSubmissions(params?: { event }): Observable<Array<DeletedSubmission>> {
    return this.http.get<Response>(`${Environment.urls.API}/core/submission/deleteSubmissions/`, { params })
      .pipe(map(v => plainToClass(DeletedSubmission, v.data as Array<DeletedSubmission>)));
  }

  public getDiscussionMessages(submission_id: number): Observable<DiscussionMessage[]> {
    return this.http.get<Response>(`${Environment.urls.API}/core/submission/${submission_id}/discussionMessages/`)
      .pipe(map(v => plainToClass(DiscussionMessage, v.data as DiscussionMessage[])));
  }

  public postDiscussionMessages(submission_id: number, user_id: number, content: string): Observable<DiscussionMessage[]> {
    const params = {
      'sender': user_id,
      'content': content
    };
    return this.http.post<Response>(`${Environment.urls.API}/core/submission/${submission_id}/discussionMessages/`, params)
      .pipe(map(v => plainToClass(DiscussionMessage, v.data as DiscussionMessage[])));
  }

  public deleteDiscussionMessage(submission_id: number, message_id: number): Observable<DiscussionMessage[]> {
    return this.http.delete<Response>(`${Environment.urls.API}/core/submission/${submission_id}/discussionMessages/${message_id}/`)
      .pipe(map(v => plainToClass(DiscussionMessage, v.data as DiscussionMessage[])));
  }


  public getRebuttal(submission: number): Observable<Rebuttal> {
    return this.http.get<Response>(`${Environment.urls.API}/core/submission/${submission}/rebuttal/`)
      .pipe(map(v => plainToClass(Rebuttal, v.data as Rebuttal)));
  }

  public writeRebuttal(submission: number, content: string): Observable<Rebuttal> {
    return this.http.post<Response>(`${Environment.urls.API}/core/submission/${submission}/rebuttal/`, { content })
      .pipe(map(v => plainToClass(Rebuttal, v.data as Rebuttal)));
  }

  public getEventBibtex(event: number, status: SubmissionStatus[], tracks: number[]): Observable<string[]> {
    return this.http.post<Response>(
      `${Environment.urls.API}/core/submission/byEvent/${event}/all/bibtex/`, {
      status, tracks
    }).pipe(map(v => v.data as string[]));
  }

  public deleteSubmissionFile(file: File, submission: Submission, updateSubmission = true): Observable<Submission> {
    const tasks: Array<Observable<Response> | Observable<Submission>> = [
      this.filesService.deleteFile(file.id)
    ];

    if (submission.status === SubmissionStatus.ACTIVE && file.trackFile.required) {
      submission.status = SubmissionStatus.PENDING;

      if (updateSubmission) {
        tasks.push(this.editSubmission({ status: SubmissionStatus.PENDING }, submission.id));
      }
    }

    return forkJoin(tasks).pipe(map(([ deleteFileResponse, editedSubmission ]: [ Response, Submission ]) => {
      if (updateSubmission) {
        return editedSubmission;
      }
      return submission;
    }));
  }

  // Dedup de chamadas concorrentes (igual a getSubmission). Pré-fetch a
  // partir do submission-view + chamada do paper-info filho compartilham.
  private publicationInfoInFlight = new Map<number, Observable<SubmissionCoverage[]>>();

  getPublicationInfo(submissionRaw: number | string): Observable<SubmissionCoverage[]> {
    if (!submissionRaw) { return; }
    const submission = typeof submissionRaw === 'string' ? parseInt(submissionRaw, 10) : submissionRaw;

    const existing = this.publicationInfoInFlight.get(submission);
    if (existing) {
      return existing;
    }

    const request$ = this.http.get<Response>(`${Environment.urls.API}/core/submission/${submission}/publication/`, {})
      .pipe(
        map(v => v.data as SubmissionCoverage[]),
        finalize(() => this.publicationInfoInFlight.delete(submission)),
        shareReplay(1),
      );

    this.publicationInfoInFlight.set(submission, request$);
    return request$;
  }

  updateSubmissionTrackFiles(fileIds: number[]): Observable<Object> {
    const params = {
      'file_ids': fileIds,
    };

    return this.http.post<Response>(`${Environment.urls.API}/core/submission/updateSubmissionTrackFiles/`, params).pipe(
      map(v => v.data as Object)
    );
  }

  public getTrackFilesMapping(target_track_id, source_track_id, user_id) : Observable<Object> {
    
    return this.http.get<Response>(`${Environment.urls.API}/core/submission/updateTrackFilesMapping/${target_track_id}/${source_track_id}/${user_id}`, { })
    .pipe(map(v => v.data as Object));
  }

  public updateTrackFilesMapping(user_id, track_target_id, track_source_id, source_file_id, target_file_id) : Observable<Object> {

    return this.http.post<Response>(`${Environment.urls.API}/core/submission/updateTrackFilesMapping/`, {
      user_id,
      track_source_id,
      track_target_id,
      source_file_id,
      target_file_id
    })
    .pipe(map(v => v.data as Object));
  }
}
